import React, { useState } from 'react';
import { IonButton, IonCheckbox, IonInput, IonItem, IonLabel, IonList, IonNote, IonSegment, IonSegmentButton, IonSelect, IonSelectOption, IonSpinner } from '@ionic/react';
import './RaceForm.css';
import { toSentenceCase, toTitleCase } from './textFormat';
import swissLocalities from '../data/swissLocalities.json';
import countries from '../data/countries.json';

interface IParticipant {
    parcours: string;
    nom: string;
    prenom: string;
    localite: string;
    anniversaire: string;
    genre: string;
    email: string;
    condition: boolean;
    pays: string;
}

interface RaceFormProps {
    id: number;
    part: IParticipant;
    setter: (field: keyof IParticipant, value: any) => void;
    save: () => void;
    present: (message: string, duration: number) => void;
    openCancel: () => void;
}

type FieldErrors = Partial<Record<keyof IParticipant, string>>;
type Locality = { name: string; zip: string; canton: string };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CURRENT_YEAR = new Date().getFullYear();
const MAX_SUGGESTIONS = 8;

const DIACRITICS = new RegExp('[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']', 'g');
const normalize = (value: string) => value.normalize('NFD').replace(DIACRITICS, '').toLowerCase();

const RaceForm: React.FC<RaceFormProps> = ({ id, part, setter, save, present, openCancel }) => {
    // The parent (RaceDashboard) mutates its state object in place and re-uses the same cached
    // React element for this form, so prop changes alone don't reliably trigger a re-render here.
    // Keeping our own copy of the field values guarantees the form always re-renders when a field
    // changes (typing, auto-capitalization, suggestion pick, ...), while `setter` still pushes every
    // change up so the parent has the data it needs to submit.
    const [local, setLocal] = useState<IParticipant>({ ...part } as IParticipant);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [suggestions, setSuggestions] = useState<Locality[]>([]);

    // Whether the participant lives in Switzerland: any value other than the "foreign" marker counts as Swiss.
    const swiss = local.pays !== "__foreign__";

    const set = (field: keyof IParticipant, value: any) => {
        setLocal(prev => ({ ...prev, [field]: value }));
        setter(field, value);
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    React.useEffect(() => {
        if ((id === 2 || id === 3 || id === 4) && local.parcours !== 'Course') {
            set("parcours", "Course");
        }
        if (local.pays === undefined || local.pays === "") {
            set("pays", "Suisse");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onCountryToggle = (value: string) => {
        set("pays", value === "swiss" ? "Suisse" : "__foreign__");
        set("localite", "");
        setSuggestions([]);
    };

    // For a foreign participant we only ask for the country, and use it as the "localité" too
    // (there's no meaningful Swiss-style locality/commune to collect for them).
    const onCountrySelect = (country: string) => {
        set("pays", country);
        set("localite", country);
    };

    const onNameInput = (field: "nom" | "prenom", value: string | null | undefined) => {
        // Capitalize live, as the user types: on-blur handling proved unreliable on iOS Safari,
        // where Ionic's shadow-DOM inputs don't always fire ionBlur predictably.
        set(field, toTitleCase(value ?? ""));
    };

    const onLocaliteInput = (value: string | null | undefined) => {
        const text = swiss ? (value ?? "") : toSentenceCase(value ?? "");
        set("localite", text);
        if (!swiss || text.trim().length < 2) {
            setSuggestions([]);
            return;
        }
        const query = normalize(text.trim());
        const matches = (swissLocalities as Locality[])
            .filter(l => normalize(l.name).includes(query))
            .slice(0, MAX_SUGGESTIONS);
        setSuggestions(matches);
    };

    const selectLocality = (locality: Locality) => {
        set("localite", locality.name);
        setSuggestions([]);
    };

    const onLocaliteBlur = () => {
        // Give click on a suggestion time to register before the list disappears.
        window.setTimeout(() => setSuggestions([]), 150);
    };

    // iOS Safari (especially as a home-screen app) doesn't reliably scroll a focused field above
    // the software keyboard on its own, so a field near the bottom of the form can end up hidden.
    // Nudge it into view ourselves once the keyboard has had time to animate in.
    const scrollFieldIntoView = (e: any) => {
        const target = e.target as HTMLElement;
        window.setTimeout(() => {
            (target.closest('ion-item') ?? target).scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 300);
    };

    const validate = (): FieldErrors => {
        const next: FieldErrors = {};
        if (id === 1 && !local.parcours) next.parcours = "Veuillez choisir un parcours";
        if (!local.nom?.trim()) next.nom = "Le nom est requis";
        if (!local.prenom?.trim()) next.prenom = "Le prénom est requis";
        if (!swiss && (!local.pays || local.pays === "__foreign__")) next.pays = "Veuillez choisir un pays";
        else if (swiss && !local.localite?.trim()) next.localite = "La localité est requise";

        const year = Number(local.anniversaire);
        if (!local.anniversaire || local.anniversaire.length !== 4 || !Number.isInteger(year) || year < 1900 || year > CURRENT_YEAR) {
            next.anniversaire = `Indiquez une année à 4 chiffres entre 1900 et ${CURRENT_YEAR}`;
        }

        if (!local.genre) next.genre = "Veuillez choisir un genre";

        if (!local.email?.trim()) next.email = "L'email est requis";
        else if (!EMAIL_PATTERN.test(local.email.trim())) next.email = "Format d'email invalide";

        if (local.condition !== true) next.condition = "Veuillez accepter les conditions d'utilisation";

        return next;
    };

    const saveForm = () => {
        const fieldErrors = validate();
        setErrors(fieldErrors);
        if (Object.keys(fieldErrors).length > 0) {
            present("Merci de corriger les champs signalés en rouge", 4000);
            return;
        }
        setSubmitting(true);
        save();
        // safety re-enable in case the network call fails silently upstream
        window.setTimeout(() => setSubmitting(false), 4000);
    };

    return (
        <div className="race-form">
            <IonList lines="full" className="race-form__list">
                {id === 1 && (
                    <IonItem className={errors.parcours ? "ion-invalid" : undefined}>
                        <IonLabel>Parcours</IonLabel>
                        <IonSelect value={local.parcours} onIonChange={e => set("parcours", e.detail.value)}>
                            <IonSelectOption value="VTT">VTT</IonSelectOption>
                            <IonSelectOption value="eVTT">VTT électrique</IonSelectOption>
                        </IonSelect>
                    </IonItem>
                )}
                {errors.parcours && <IonNote color="danger" className="race-form__error">{errors.parcours}</IonNote>}

                {id === 2 && (
                    <IonItem lines="none">
                        <IonLabel><strong>Course à pied</strong></IonLabel>
                    </IonItem>
                )}

                <IonItem className={errors.nom ? "ion-invalid" : undefined}>
                    <IonLabel position="floating">Nom</IonLabel>
                    <IonInput autoCapitalize="words" clearInput value={local.nom} onIonChange={e => onNameInput("nom", e.detail.value)} onIonFocus={scrollFieldIntoView} />
                </IonItem>
                {errors.nom && <IonNote color="danger" className="race-form__error">{errors.nom}</IonNote>}

                <IonItem className={errors.prenom ? "ion-invalid" : undefined}>
                    <IonLabel position="floating">Prénom</IonLabel>
                    <IonInput autoCapitalize="words" clearInput value={local.prenom} onIonChange={e => onNameInput("prenom", e.detail.value)} onIonFocus={scrollFieldIntoView} />
                </IonItem>
                {errors.prenom && <IonNote color="danger" className="race-form__error">{errors.prenom}</IonNote>}

                <IonItem lines="none" className="race-form__segment-item">
                    <IonSegment value={swiss ? "swiss" : "foreign"} onIonChange={e => onCountryToggle(e.detail.value as string)}>
                        <IonSegmentButton value="swiss">
                            <IonLabel>Suisse</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="foreign">
                            <IonLabel>Autre</IonLabel>
                        </IonSegmentButton>
                    </IonSegment>
                </IonItem>

                {!swiss && (
                    <>
                        <IonItem className={errors.pays ? "ion-invalid" : undefined}>
                            <IonLabel position="floating">Pays</IonLabel>
                            <IonSelect value={local.pays === "__foreign__" ? undefined : local.pays} onIonChange={e => onCountrySelect(e.detail.value)}>
                                {(countries as string[]).map(country => (
                                    <IonSelectOption key={country} value={country}>{country}</IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonItem>
                        {errors.pays && <IonNote color="danger" className="race-form__error">{errors.pays}</IonNote>}
                    </>
                )}

                {swiss && <div className="race-form__autocomplete">
                    <IonItem className={errors.localite ? "ion-invalid" : undefined}>
                        <IonLabel position="floating">Localité</IonLabel>
                        <IonInput
                            autoCapitalize="sentences"
                            clearInput
                            autocomplete="off"
                            value={local.localite}
                            onIonChange={e => onLocaliteInput(e.detail.value!)}
                            onIonBlur={onLocaliteBlur}
                            onIonFocus={scrollFieldIntoView}
                        />
                    </IonItem>
                    {suggestions.length > 0 && (
                        <IonList className="race-form__suggestions" lines="full">
                            {suggestions.map(s => (
                                <IonItem key={s.name + s.zip} button onMouseDown={() => selectLocality(s)}>
                                    <IonLabel>{s.name} <IonNote>{s.zip} {s.canton}</IonNote></IonLabel>
                                </IonItem>
                            ))}
                        </IonList>
                    )}
                </div>}
                {swiss && errors.localite && <IonNote color="danger" className="race-form__error">{errors.localite}</IonNote>}

                <IonItem className={errors.anniversaire ? "ion-invalid" : undefined}>
                    <IonLabel position="floating">Année de naissance</IonLabel>
                    <IonInput
                        type="number"
                        inputmode="numeric"
                        maxlength={4}
                        min="1900"
                        max={String(CURRENT_YEAR)}
                        placeholder="ex: 1990"
                        value={local.anniversaire}
                        onIonChange={e => set("anniversaire", e.detail.value!)}
                        onIonFocus={scrollFieldIntoView}
                    />
                </IonItem>
                {errors.anniversaire && <IonNote color="danger" className="race-form__error">{errors.anniversaire}</IonNote>}

                <IonItem lines="none" className="race-form__segment-item race-form__segment-item--stacked">
                    <IonLabel className="race-form__segment-label">Genre</IonLabel>
                    <IonSegment value={local.genre || undefined} onIonChange={e => set("genre", e.detail.value)}>
                        <IonSegmentButton value="Femme">
                            <IonLabel>Femme</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="Homme">
                            <IonLabel>Homme</IonLabel>
                        </IonSegmentButton>
                    </IonSegment>
                </IonItem>
                {errors.genre && <IonNote color="danger" className="race-form__error">{errors.genre}</IonNote>}

                <IonItem className={errors.email ? "ion-invalid" : undefined}>
                    <IonLabel position="floating">Email</IonLabel>
                    <IonInput type="email" inputmode="email" clearInput value={local.email} onIonChange={e => set("email", e.detail.value!)} onIonFocus={scrollFieldIntoView} />
                </IonItem>
                {errors.email && <IonNote color="danger" className="race-form__error">{errors.email}</IonNote>}

                <IonItem lines="none" className={errors.condition ? "ion-invalid" : undefined}>
                    <IonCheckbox
                        slot="start"
                        checked={local.condition}
                        onIonChange={e => set("condition", e.detail.checked)}
                    />
                    <IonLabel className="ion-text-wrap">J'accepte les conditions d'utilisation</IonLabel>
                </IonItem>
                {errors.condition && <IonNote color="danger" className="race-form__error">{errors.condition}</IonNote>}
            </IonList>

            <IonButton expand="full" onClick={saveForm} disabled={submitting} className="race-form__submit">
                {submitting ? <IonSpinner name="dots" /> : "Démarrer"}
            </IonButton>
            <IonButton expand="full" fill="clear" onClick={openCancel} color="danger">Retour</IonButton>
        </div>
    );
};

export default RaceForm;
